import clsx from 'clsx';
import * as React from 'react';
import {
    type CheckboxGroupAria,
    useCheckboxGroup as useAriaCheckboxGroup,
    useObjectRef,
} from 'react-aria';
import {
    type CheckboxGroupProps as AriaCheckboxGroupProps,
    type CheckboxGroupState,
    useCheckboxGroupState,
} from 'react-stately';

import { useMergedRefs } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { useAriaFieldProps } from '../Form';

const CheckboxGroupContext = React.createContext<CheckboxGroupState | null>(null);

/**
 * Hook to get the current checkbox group state from context.
 */
export function useCheckboxGroupContext() {
    const context = React.useContext(CheckboxGroupContext);
    assert(context, 'Must be used within a CheckboxGroup');
    return context;
}

export type CheckboxGroupRef = {
    focus: () => void;
};

export type CheckboxGroupHookProps = AriaCheckboxGroupProps & {
    ref: React.Ref<CheckboxGroupRef> | undefined;
    children: React.ReactNode;
};

export type CheckboxGroupHookResult = {
    state: CheckboxGroupState;
    ref: React.RefCallback<HTMLDivElement>;
    groupProps: CheckboxGroupAria['groupProps'] & {
        children: React.ReactElement;
    };
};

/**
 * Low-level hook to create a card checkbox group component.
 */
export function useCheckboxGroup({
    ref,
    children,
    ...props
}: CheckboxGroupHookProps): CheckboxGroupHookResult {
    const containerRef = useObjectRef(ref);
    const fieldProps = useAriaFieldProps(props);
    const state = useCheckboxGroupState(fieldProps);
    const { groupProps } = useAriaCheckboxGroup(fieldProps, state);
    const mergedRef = useMergedRefs(containerRef, fieldProps.ref);

    return {
        ref: mergedRef,
        state,
        groupProps: {
            ...groupProps,
            children: (
                <CheckboxGroupContext.Provider value={state}>
                    {children}
                </CheckboxGroupContext.Provider>
            ),
        },
    };
}

export type CheckboxGroupProps = Omit<CheckboxGroupHookProps, 'ref'> & {
    className?: string;
    style?: React.CSSProperties;
    /**
     * Orientation of the checkbox group.
     * @default 'vertical'
     */
    orientation?: 'vertical' | 'horizontal';
};
export type CheckboxGroupPropsWithRef = CheckboxGroupProps & { ref?: React.Ref<HTMLDivElement> };

/**
 * Checkbox group component to use a group of checkbox buttons.
 */
export function CheckboxGroup({
    ref: forwardedRef,
    ...props
}: CheckboxGroupProps & { ref?: React.ForwardedRef<CheckboxGroupRef> }) {
    const { ref, groupProps } = useCheckboxGroup({ ...props, ref: forwardedRef });
    return (
        <div
            ref={ref}
            {...groupProps}
            className={clsx('flex gap-3 aria-[orientation=vertical]:flex-col', props.className)}
            style={props.style}
            aria-orientation={props.orientation ?? 'vertical'}
        />
    );
}
